// 输入辅助模块 - 创建 WASD 键控制对象和输入字段
import UIStyleHelper from './UIStyleHelper.js';

export default class InputHelper {
    /** 标准菜单按键提示（供 UI 面板引用） */
    static MENU_HINTS = UIStyleHelper.MENU_HINTS;

    /**
     * 创建 WASD 键控制对象（替代方向键）
     * 返回一个与 Phaser cursors 兼容的对象
     * @param {Phaser.Input.Keyboard.KeyboardPlugin} keyboard - Phaser 键盘插件
     * @returns {Object} 包含 up, down, left, right 键的对象
     */
    static createWASDKeys(keyboard) {
        return {
            up: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W),
            down: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S),
            left: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
            right: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D)
        };
    }
    
    /**
     * 创建 WASD 键（用于菜单导航，只返回上下键）
     * @param {Phaser.Input.Keyboard.KeyboardPlugin} keyboard - Phaser 键盘插件
     * @returns {Object} 包含 up, down 键的对象
     */
    static createWASDKeysForMenu(keyboard) {
        return {
            up: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W),
            down: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S)
        };
    }

    /**
     * 处理单次菜单按键（W/S + 方向键 + A/D + Space/Enter + Esc）
     * @param {KeyboardEvent} event
     * @param {Object} handlers
     * @param {Object} options
     * @returns {boolean} 是否已处理
     */
    static processMenuKey(event, handlers = {}, options = {}) {
        const {
            vertical = true,
            horizontal = false,
            confirm = true,
            cancel = true
        } = options;

        if (handlers.blockWhen?.()) {
            return false;
        }

        switch (event.code) {
            case 'KeyW':
            case 'ArrowUp':
                if (vertical && handlers.onUp) {
                    handlers.onUp();
                    return true;
                }
                break;
            case 'KeyS':
            case 'ArrowDown':
                if (vertical && handlers.onDown) {
                    handlers.onDown();
                    return true;
                }
                break;
            case 'KeyA':
            case 'ArrowLeft':
                if (horizontal && handlers.onLeft) {
                    handlers.onLeft();
                    return true;
                }
                break;
            case 'KeyD':
            case 'ArrowRight':
                if (horizontal && handlers.onRight) {
                    handlers.onRight();
                    return true;
                }
                break;
            case 'Space':
            case 'Enter':
                if (confirm && handlers.onSelect) {
                    handlers.onSelect();
                    return true;
                }
                break;
            case 'Escape':
                if (cancel && handlers.onCancel) {
                    handlers.onCancel();
                    return true;
                }
                break;
            case 'KeyQ':
            case 'BracketLeft':
                if (handlers.onAuxPrev) {
                    handlers.onAuxPrev();
                    return true;
                }
                break;
            case 'KeyE':
            case 'BracketRight':
                if (handlers.onAuxNext) {
                    handlers.onAuxNext();
                    return true;
                }
                break;
        }

        return false;
    }

    /**
     * 绑定标准菜单键盘，返回可 destroy 的控制器
     * @param {Phaser.Scene} scene
     * @param {Object} handlers
     * @param {Object} options - vertical / horizontal / confirm / cancel
     * @returns {{ destroy: Function }}
     */
    static bindMenuKeyboard(scene, handlers = {}, options = {}) {
        const handler = (event) => {
            this.processMenuKey(event, handlers, options);
        };

        scene.input.keyboard.on('keydown', handler);

        return {
            destroy: () => {
                scene.input.keyboard.off('keydown', handler);
            }
        };
    }
    
    /**
     * 创建输入字段（标签 + 输入框）
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} key - 字段键名（用于存储值）
     * @param {string} label - 标签文本
     * @param {string} defaultValue - 默认值
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {Object} options - 选项
     * @param {number} options.labelWidth - 标签宽度（默认150）
     * @param {number} options.inputWidth - 输入框宽度（默认200）
     * @param {number} options.inputHeight - 输入框高度（默认30）
     * @param {Object} options.labelStyle - 标签样式（可选）
     * @param {Object} options.inputStyle - 输入框文本样式（可选）
     * @returns {Object} {label: Text, inputBg: Graphics, inputText: Text, value: string}
     */
    static createInputField(scene, key, label, defaultValue, x, y, options = {}) {
        const {
            labelWidth = 150,
            inputWidth = 200,
            inputHeight = 30,
            labelStyle = {},
            inputStyle = {}
        } = options;
        
        // 创建标签
        const labelText = UIStyleHelper.createLabel(
            scene,
            label,
            x - labelWidth,
            y,
            labelStyle
        );
        
        // 创建输入框背景
        const inputBg = scene.add.graphics();
        inputBg.fillStyle(0x333333, 1);
        inputBg.fillRect(x, y - inputHeight / 2, inputWidth, inputHeight);
        inputBg.lineStyle(2, 0x4cc9f0, 1);
        inputBg.strokeRect(x, y - inputHeight / 2, inputWidth, inputHeight);
        inputBg.setInteractive(
            new Phaser.Geom.Rectangle(x, y - inputHeight / 2, inputWidth, inputHeight),
            Phaser.Geom.Rectangle.Contains
        );
        
        // 创建输入框文本
        const defaultInputStyle = {
            fontSize: '16px',
            fill: '#ffffff',
            fontFamily: 'monospace',
            backgroundColor: 'transparent'
        };
        const finalInputStyle = { ...defaultInputStyle, ...inputStyle };
        
        const inputText = scene.add.text(
            x + 10,
            y,
            defaultValue || '',
            finalInputStyle
        );
        inputText.setOrigin(0, 0.5);
        
        // 存储当前值
        const fieldData = {
            key: key,
            value: defaultValue || '',
            label: labelText,
            inputBg: inputBg,
            inputText: inputText
        };
        
        // 添加点击事件（聚焦输入框）
        inputBg.on('pointerdown', () => {
            inputBg.clear();
            inputBg.fillStyle(0x444444, 1);
            inputBg.fillRect(x, y - inputHeight / 2, inputWidth, inputHeight);
            inputBg.lineStyle(2, 0xffff00, 1);
            inputBg.strokeRect(x, y - inputHeight / 2, inputWidth, inputHeight);
        });
        
        return fieldData;
    }
    
    /**
     * 更新输入字段的值
     * @param {Object} fieldData - 输入字段数据对象
     * @param {string} newValue - 新值
     */
    static updateInputField(fieldData, newValue) {
        if (fieldData && fieldData.inputText) {
            fieldData.value = newValue;
            fieldData.inputText.setText(newValue);
        }
    }
    
    /**
     * 创建标准菜单控制（兼容旧接口，内部使用 bindMenuKeyboard）
     * @param {Phaser.Scene} scene - 场景对象
     * @param {Object} handlers - 事件处理器
     * @returns {Object} 控制对象（含 destroy）
     */
    static createMenuControls(scene, handlers = {}) {
        return this.bindMenuKeyboard(scene, handlers, {
            vertical: true,
            horizontal: Boolean(handlers.onLeft || handlers.onRight),
            confirm: Boolean(handlers.onSelect),
            cancel: Boolean(handlers.onCancel)
        });
    }
}
